import * as amplitude from '@amplitude/analytics-browser';
import { isPaidUser } from './linemate-react-common/src/util';

let initialized = false;

/**
 * Sets GA4's reserved User-ID for Google tags that are loaded by GTM. The
 * website uses the opaque Linemate user UUID, never Firebase's UID or PII.
 */
export const setGoogleAnalyticsUserId = (userId = null) => {
    if (typeof window === 'undefined') {
        return;
    }

    const normalizedUserId = typeof userId === 'string' && userId.length > 0
        ? userId
        : null;

    // A direct Google tag may be supplied by GTM or another deployment path.
    // `set` changes the ID for all following events without emitting an event.
    if (typeof window.gtag === 'function') {
        window.gtag('set', { user_id: normalizedUserId });
    }

    // A GTM Google tag can read this Data Layer variable through its configuration
    // settings. Keeping it current also makes it available to later GTM events.
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ user_id: normalizedUserId });
};

export const getAmplitude = (userId) => {
    if (!initialized) {
        amplitude.init(
            process.env.REACT_APP_AMPLITUDE_API_KEY, 
            {   
                userId: userId,
                autocapture: {
                    webVitals: false
                }
            }
        );
        initialized = true;
    } else if (userId !== undefined) {
        amplitude.setUserId(userId || undefined);
    }
    return amplitude;
}

function buildDefaultEventProps(analyticsContext = {}) {
    const defaultProps = {};

    if (analyticsContext?.league) {
        defaultProps.league = analyticsContext.league;
    }

    if (analyticsContext?.userAttributes) {
        defaultProps.isPaidUser = isPaidUser(analyticsContext.userAttributes);
        defaultProps.is_paid_user = isPaidUser(analyticsContext.userAttributes);
    }

    return defaultProps;
}

function getAmplitudeInstance(analyticsContext = {}) {
    return analyticsContext?.amplitudeInstance || null;
}

export const logCustomEvent = (
    analyticsContext,
    eventName,
    opts
) => {
    try {
        const amplitudeInstance = getAmplitudeInstance(analyticsContext);
        if (!amplitudeInstance || !eventName) {
            return;
        }

        const defaultProps = buildDefaultEventProps(analyticsContext);
        const mergedProps = opts && typeof opts === 'object' && !Array.isArray(opts)
            ? {
                ...defaultProps,
                ...opts
            }
            : (Object.keys(defaultProps).length > 0 ? defaultProps : opts);

        amplitudeInstance.track(eventName, mergedProps);
    } catch (error) {
        console.error(error);
    }
}

export const trackAdImpression = (
    analyticsContext,
    {
        operator,
        affiliate,
        source,
        additionalProperties = {}
    } = {}
) => {
    const normalizedOperator = operator || affiliate;
    if (!getAmplitudeInstance(analyticsContext) || !normalizedOperator || !source) {
        return;
    }

    logCustomEvent(analyticsContext, 'ad_impression', {
        operator: normalizedOperator,
        affiliate: affiliate || normalizedOperator,
        source,
        ...additionalProperties
    });
}
